import { ExternalLink, FileText } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { truncatefileNameText } from '../../../lib/utils';


const getFileIcon = (filename: string) => {
    const extension = filename.split('.').pop()?.toLowerCase();
    if (extension === 'pdf') {
        return <FileText className="h-4 w-4 ml-2 text-[#737373] flex-shrink-0" />;
    } else if (['jpg', 'jpeg', 'png', 'gif'].includes(extension || '')) {
        return <FileText className="h-4 w-4 ml-2 text-[#737373] flex-shrink-0" />;
    } else if (['doc', 'docx'].includes(extension || '')) {
        return <FileText className="h-4 w-4 ml-2 text-[#737373] flex-shrink-0" />;
    }
    return <ExternalLink className="h-4 w-4 ml-2 text-[#737373] flex-shrink-0" />;
};


export const EvidenceLinks: React.FC<{ links: string[] }> = ({ links }) => {
    if (!links || links.length === 0) {
        return (
            <div className="text-sm text-gray-400 italic">
                No evidence files attached
            </div>
        );
    }

    return (
        <div className="flex flex-wrap gap-2">
            {links.map((link, index) => {
                const filename = link;
                const displayName = truncatefileNameText(filename, 20);
                
                return (
                    <div 
                        key={index} 
                        className="bg-white border border-[#E5E5E5] rounded-lg px-[10px] py-1 flex items-center text-xs hover:shadow-sm hover:border-gray-200 transition-all duration-200 group relative min-w-0"
                    >
                        {/* Tooltip for full filename */}
                        {filename !== displayName && (
                            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-md opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-20 shadow-lg">
                                {filename}
                                <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
                            </div>
                        )}
                        <a 
                            href={link} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-[#0A0A0A] hover:text-blue-600 transition-colors font-medium truncate"
                        >
                            {displayName}
                        </a>
                        {getFileIcon(filename)}
                    </div>
                );
            })}
        </div>
    );
};


export const EvidenceReport: React.FC<{ data: any[] }> = ({ data }) => {
    if (!data || data.length === 0) {
        return <div className="text-center py-8 text-gray-500">No evidence data available.</div>;
    }

    return (
        <div className="bg-slate-100 border border-[#E5E5E5] rounded-xl shadow-sm overflow-hidden">
            <div className="bg-slate-100">
                <div className="grid grid-cols-12 gap-4 px-2 py-3">
                    <div className="col-span-4 md:col-span-3">
                        <span className="text-sm font-medium text-[#737373]">Evidence Source</span>
                    </div>
                    <div className="col-span-8 md:col-span-9">
                        <span className="text-sm font-medium text-[#737373]">Evidence Links</span>
                    </div>
                </div>
            </div>
            <div className="divide-y divide-gray-100">
                {data.map((interview, index) => (
                    <div 
                        key={`evidence-${index}`}
                        className={cn("grid grid-cols-12 gap-4 px-2 py-4 transition-all duration-200 bg-white")}
                    >
                        <div className="col-span-4 md:col-span-3 flex items-start pt-1">
                            <span className="text-sm font-medium text-[#0A0A0A]">
                                Interviews - {interview.interview?.interviewee_type || 'Unknown Interview'}
                            </span>
                        </div>
                        <div className="col-span-8 md:col-span-9">
                            <EvidenceLinks links={interview.interview?.evidence_links || []} />
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};